import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import { useSmartTranslation } from '../../hooks/useSmartTranslation'
import { outcomeOf, previewLabel } from './labelPrintingApi'
import { LAYOUT_TEXT, OUTCOME_TEXT, labelSizeText, textOf } from './labelPrintingTexts'

export const PREVIEW_DELAY_MS = 400

// Historia 226 (D7, RN40-RN43, CU9; S174-S180): the label as Sun would print it on the chosen printer, for the
// dialog's CURRENT selection (not saved: no template is written). Only while the tab is open; each change of printer
// or selection asks again after 400 ms without changes. A late answer to an older request is dropped.
const QrPrinterPreview = ({ active, request }) => {
  const { t_ } = useSmartTranslation()
  const [preview, setPreview] = useState({})
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const latest = useRef(0)
  const key = JSON.stringify(request)

  useEffect(() => {
    if (!active || request.printerId === '') return undefined
    const timer = setTimeout(async () => {
      const ticket = latest.current + 1
      latest.current = ticket
      setLoading(true)
      try {
        const response = await previewLabel(JSON.parse(key))
        if (ticket !== latest.current) return
        setPreview(response)
        setError('')
      } catch (failure) {
        if (ticket !== latest.current) return
        const outcome = outcomeOf(failure)
        setPreview({})
        setError(outcome === 'QR_DOES_NOT_FIT' ? 'The QR does not fit on this label' : (OUTCOME_TEXT[outcome] || 'Unexpected error'))
      } finally {
        if (ticket === latest.current) setLoading(false)
      }
    }, PREVIEW_DELAY_MS)
    return () => clearTimeout(timer)
  }, [active, key, request.printerId])

  if (request.printerId === '') {
    return <Typography sx={{ color: '#9a9a9a', p: 2 }}>{t_('Choose a printer to see the label')}</Typography>
  }

  const unresolved = preview.unresolvedFields || []
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 1, width: '100%' }}>
      {loading && <CircularProgress size={20} />}
      {error && <Typography sx={{ color: '#ff8a80' }}>{t_(error)}</Typography>}
      {!error && preview.imagePng && (
        <>
          <Box
            component='img'
            alt={t_('Printer view')}
            src={`data:image/png;base64,${preview.imagePng}`}
            // E11: fills the panel's width keeping the label's proportion, scaled without smoothing so the text stays crisp
            data-aspect-ratio={`${preview.labelWidthMm}/${preview.labelHeightMm}`}
            sx={{ bgcolor: '#fff', imageRendering: 'pixelated', width: '100%', height: 'auto', aspectRatio: `${preview.labelWidthMm} / ${preview.labelHeightMm}`, border: '1px solid #777' }}
          />
          <Typography sx={{ fontSize: 13, color: '#9a9a9a' }}>
            {`${labelSizeText(preview)} · ${preview.dpi} ${t_('dpi')} · ${t_(textOf(LAYOUT_TEXT, preview.layout))}`}
          </Typography>
          {preview.linesDropped > 0 && (
            <Alert severity='warning'>{t_('{{count}} lines will be cut', { count: preview.linesDropped })}</Alert>
          )}
          {unresolved.length > 0 && (
            <Alert severity='info'>{t_('Fields not printed: {{fields}}', { fields: unresolved.join(', ') })}</Alert>
          )}
        </>
      )}
    </Box>
  )
}

export default QrPrinterPreview
